import * as React from 'react';

import logoFavo from '@/design/logo-favo.svg';
import { MenuDaConta } from '@/layout/menu-conta';
import { cn } from '@/lib/utils';

/* Wrap — o casco. Duas formas, e o que muda entre elas é ONDE a marca fica.
 *
 * NÃO existe barra lateral de navegação nem cabeçalho fixo. A navegação entre
 * etapas é a trilha, e o painel leva as próprias ações. Casco de aplicação
 * (menu lateral + topo grudado) espalhava a atenção e roubava largura de telas
 * que já são densas de número.
 *
 * ── POR QUE DUAS FORMAS ────────────────────────────────────────────────────
 * `topo` é o casco de sempre: marca em cima, uma coluna só embaixo. É o certo
 * para PAINEL e DOCUMENTO (preço, relatório, histórico), onde o conteúdo é
 * tabela e gráfico e usa toda a largura que houver.
 *
 * `lateral` parte a tela ao MEIO: marca de um lado, trabalho do outro, cada
 * um centrado na sua metade. Existe por uma razão medida, não por gosto: num
 * formulário os controles têm largura pela natureza do dado (208px para
 * moeda), então numa faixa de 1360px sobra meia tela de vazio à direita de
 * cada campo e o olho perde o par rótulo/valor no caminho.
 *
 * As metades são metades de verdade (1fr 1fr). Quem tem teto é o CONTEÚDO
 * delas: a etapa para de crescer em `--largura-forma` e passa a centrar na
 * própria metade, em vez de encostar nas duas bordas do monitor.
 *
 * Centrado nos DOIS eixos, dos dois lados: a marca fica no meio da janela
 * (`.coluna-marca`), e por isso a etapa que não enche a janela fica no meio
 * dela também (`.coluna-forma` + `content-center`). Etapa alta ignora as duas
 * coisas e começa no topo, como sempre.
 *
 * Abaixo de `largo` (1180px) as duas formas são a MESMA coisa: não há largura
 * para dividir, e o rail volta a ser a linha do topo. O HTML é um só; quem
 * troca linha por coluna é o CSS (`.coluna-marca`). Dois blocos de marcação
 * para o mesmo cabeçalho seria a garantia de que um dos dois envelheceria. */
export function Wrap({
  marca = 'topo',
  children,
}: {
  /** `lateral` só nas telas de formulário. Quem decide é `Pagina`, pela etapa. */
  marca?: 'topo' | 'lateral' | undefined;
  children: React.ReactNode;
}) {
  const lateral = marca === 'lateral';

  return (
    <div
      className={cn(
        'mx-auto w-full px-4 py-6 mesa:px-8 mesa:py-8',
        lateral
          ? 'max-w-app-forma largo:grid largo:grid-cols-2 largo:items-start largo:gap-folga-marca'
          : 'max-w-app',
      )}
    >
      <header
        className={cn(
          'flex items-end justify-between gap-4',
          lateral
            ? 'coluna-marca mb-7 largo:mb-0 largo:flex-col largo:items-center largo:justify-center largo:gap-5'
            : 'mb-7',
        )}
        data-nao-imprimir=""
      >
        <div
          className={cn(
            'grid min-w-0 justify-items-start gap-1.5',
            lateral && 'largo:justify-items-center largo:text-center',
          )}
        >
          {/* O logotipo cresce na metade: os 30px que equilibravam uma linha
              de topo viram um selo perdido no meio de meia tela. */}
          <img
            src={logoFavo}
            alt="FAVO Educação"
            className={cn('h-[30px] w-auto', lateral && 'largo:h-[38px]')}
          />
          {/* Na metade, o nome sobe um degrau da rampa: os 16px que
              equilibravam a linha do topo ficavam miúdos embaixo de um
              logotipo de 38px. Continua abaixo de `.titulo-tela` (26/700), que
              é quem manda na coluna ao lado. */}
          <span
            className={cn(
              'text-sub font-semibold',
              lateral && 'largo:text-secao largo:leading-snug',
            )}
          >
            Auditor de Precificação
          </span>
        </div>
        {/* Na metade, a conta desce para baixo da marca: são a mesma pergunta
            (quem sou eu, em que ferramenta estou) e leem como um bloco só.
            SEM divisória. Havia um fio de largura total aqui, e um fio só
            separa quando existe algo do outro lado para separar: nesta forma
            a marca flutua sozinha no meio de meia tela vazia, então a linha
            atravessava o nada com um botão pequeno pendurado embaixo. Quem
            agrupa os dois é o espaço, que aqui é o único vizinho. */}
        <div className={cn(lateral && 'largo:flex largo:w-full largo:justify-center')}>
          <MenuDaConta />
        </div>
      </header>

      {/* @container: daqui para dentro, "cabe a segunda coluna?" é pergunta
          para a COLUNA, não para a janela. Ver --container-coluna-larga em
          globals.css: com a marca ao lado, os dois números deixaram de ser o
          mesmo, e telas que perguntavam à janela passariam a espremer
          cartão. */}
      <main
        id="conteudo"
        className={cn(
          '@container',
          lateral &&
            'coluna-forma largo:mx-auto largo:grid largo:w-full largo:max-w-forma largo:content-center',
        )}
      >
        {children}
      </main>
    </div>
  );
}
